import RegressionModel from "@/types/RegressionModel";
import * as Sentry from "@sentry/react";
import * as d3Regression from "d3-regression";
import { arima } from "./stats/arima";
import { movingAverage, scottMovingAverage } from "./stats/movingAvg";

export type RegressionData = {
  x: number;
  y: number;
};

type RegressionPoints = [number, number][];

interface RegresssionLinearResult extends RegressionPoints {
  a: number;
  b: number;
  predict: (arg: number) => number | null;
  rSquared: number;
}

interface RegressionPolyResult extends RegressionPoints {
  coefficients: number[];
  predict: (arg: number) => number | null;
  rSquared: number;
}

export interface RegressionCustomResult extends RegressionPoints {
  predict: (arg: number) => number | null;
}

export type AnyRegressionResult =
  | RegresssionLinearResult
  | RegressionPolyResult
  | RegressionCustomResult;

export function regression(
  regressionType: RegressionModel,
  data: RegressionData[],
  domain?: number[],
) {
  switch (regressionType) {
    case RegressionModel.Linear:
      return d3Regression
        .regressionLinear()
        .x((d: RegressionData) => d.x)
        .y((d: RegressionData) => d.y)
        .domain(domain)(data) as RegresssionLinearResult;
    case RegressionModel.Poly3:
      return d3Regression
        .regressionPoly()
        .order(3)
        .x((d: RegressionData) => d.x)
        .y((d: RegressionData) => d.y)
        .domain(domain)(data) as RegressionPolyResult;
    case RegressionModel.Poly4:
      return d3Regression
        .regressionPoly()
        .order(4)
        .x((d: RegressionData) => d.x)
        .y((d: RegressionData) => d.y)
        .domain(domain)(data) as RegressionPolyResult;
    case RegressionModel.Loess:
      let result = d3Regression
        .regressionLoess()
        .x((d: RegressionData) => d.x)
        .y((d: RegressionData) => d.y)
        .bandwidth(0.5)(data);

      // add a predict method to the result
      let loessResult = result as RegressionCustomResult;
      loessResult.predict = predictFromNearestLess(result);
      return loessResult;

    case RegressionModel.MovingAverage: {
      const points = data.map((d) => [d.x, d.y]) as [number, number][];

      let maResult = movingAverage(points, 30) as any as RegressionCustomResult;
      maResult.predict = predictFromNearestLess(maResult);
      return maResult as AnyRegressionResult;
    }

    case RegressionModel.ScottMovingAverage: {
      const points = data.map((d) => [d.x, d.y]) as [number, number][];
      let maResult = scottMovingAverage(points, 30) as any as RegressionCustomResult;
      maResult.predict = predictFromNearestLess(maResult);
      return maResult as AnyRegressionResult;
    }

    case RegressionModel.Arima: {
      const points = data.map((d) => d.y) as number[];
      let arimaResult = arima(points, 1, 0, 1) as any as RegressionCustomResult;
      arimaResult.predict = predictFromNearestLess(arimaResult);
      return arimaResult as AnyRegressionResult;
    }
  }
}

function predictFromNearestLess(result: RegressionPoints) {
  const points = Array.from(result);

  return (x: number) => {
    if (points.length === 0) {
      Sentry.captureEvent({
        message: "predictFromNearestLess points is empty",
        level: "warning",
      });
      return null;
    }
    const sorted = points.sort((a, b) => Math.abs(a[0] - x) - Math.abs(b[0] - x));
    const nearest = sorted.find((d) => d[0] <= x);
    if (!nearest) {
      return sorted[0][1];
    }
    return nearest[1];
  };
}

export function getStandardDeviation(array: (number | null | undefined)[]): number | undefined {
  const validNumbers = array.filter((x): x is number => typeof x === "number" && !Number.isNaN(x));

  const n = validNumbers.length;
  if (n < 2) {
    return undefined;
  }

  const mean = validNumbers.reduce((a, b) => a + b) / n;
  return Math.sqrt(validNumbers.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n);
}
